/**
=========================================================
* Trust Cargo - the one text field
=========================================================

* Every text box, select and date in the system is drawn here, and only
* here. Screens used to bring their own: a 52px box with a thick frame and
* the label squeezed onto its border on the forms, a 48px one on the
* notification settings, another on the shipping marks - the same field
* looked different from one window to the next, and none of them read
* easily.
*
* So: a 40px box (34px at size="small", in tables), a thin neutral frame,
* the label above it (see inputLabel.js), the value in plain dark text and
* the placeholder clearly lighter, a gold frame with a soft ring while
* typing, red when something is wrong, and grey when it cannot be changed.
*/

import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

import pxToRem from "assets/theme/functions/pxToRem";

const { dark, white, grey, inputColors, error } = colors;
const { fontWeightRegular } = typography;
const { borderRadius } = borders;

const inputBase = {
  styleOverrides: {
    root: {
      display: "grid !important",
      placeItems: "center !important",
      width: "100% !important",
      height: "auto !important",
      minHeight: pxToRem(40),
      padding: `${pxToRem(8)} ${pxToRem(12)}`,
      fontSize: `${pxToRem(14)} !important`,
      fontWeight: `${fontWeightRegular} !important`,
      lineHeight: "1.4 !important",
      color: `${dark.main} !important`,
      backgroundColor: `${white.main} !important`,
      backgroundClip: "padding-box !important",
      border: `1px solid ${inputColors.borderColor.main}`,
      appearance: "none !important",
      borderRadius: borderRadius.md,
      transition: "box-shadow 150ms ease, border-color 150ms ease !important",

      "&:hover": {
        borderColor: inputColors.borderColor.hover,
      },

      "&.Mui-focused": {
        borderColor: inputColors.borderColor.focus,
        boxShadow: `0 0 0 3px ${rgba(inputColors.borderColor.focus, 0.18)}`,
      },

      "&.Mui-error": {
        borderColor: error.main,
      },
      "&.Mui-error.Mui-focused": {
        boxShadow: `0 0 0 3px ${rgba(error.main, 0.15)}`,
      },

      "&.Mui-disabled": {
        backgroundColor: `${grey[100]} !important`,
        borderColor: inputColors.borderColor.main,
        color: `${rgba(dark.main, 0.5)} !important`,
      },

      // the compact size, for boxes inside table rows
      "&.MuiInputBase-sizeSmall": {
        minHeight: pxToRem(34),
        padding: `${pxToRem(5)} ${pxToRem(10)}`,
      },

      // a multi-line box grows with its text instead of centring one line
      "&.MuiInputBase-multiline": {
        placeItems: "stretch !important",
      },
    },

    input: {
      width: "100% !important",
      height: `${pxToRem(22)}`,
      padding: "0 !important",

      // a dropdown keeps its text off the arrow - stated here because the
      // padding above would otherwise win over select.js / nativeSelect.js
      "&.MuiSelect-select, &.MuiNativeSelect-select": {
        paddingRight: `${pxToRem(24)} !important`,
      },

      // lighter than a value, so an empty box never looks filled in
      "&::placeholder": {
        color: `${rgba(dark.main, 0.4)} !important`,
        opacity: "1 !important",
      },
      // MUI hides the placeholder until the label moves out of the way;
      // here the label is always above the box, so it never has to
      "label[data-shrink=false] + .MuiInputBase-formControl &::placeholder": {
        opacity: "1 !important",
      },
    },
  },
};

export default inputBase;
